import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, MapPin, User, Package, CheckCircle, XCircle, RefreshCw, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { UserPickup } from "@/types/database";

interface PackageInfo {
  id: string;
  retailer: string;
  size: string;
}

interface PickupWithDetails extends UserPickup {
  packages: PackageInfo[];
  userName: string;
  userEmail: string;
}

const TIME_SLOTS = [
  "9:00 AM - 12:00 PM",
  "12:00 PM - 3:00 PM",
  "3:00 PM - 6:00 PM",
];

interface CounterProposalManagerProps {
  onPickupUpdated?: () => void;
}

export const CounterProposalManager = ({ onPickupUpdated }: CounterProposalManagerProps) => {
  const [pickups, setPickups] = useState<PickupWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedPickup, setSelectedPickup] = useState<PickupWithDetails | null>(null);
  const [newDate, setNewDate] = useState<Date | undefined>();
  const [newTimeSlot, setNewTimeSlot] = useState<string>("");

  const fetchCounterProposals = async () => {
    try {
      // Fetch pickups with counter_proposed or user_denied status
      const { data: pickupData, error: pickupError } = await (supabase as any)
        .from("user_pickups")
        .select("*")
        .in("status", ["counter_proposed", "user_denied"])
        .order("updated_at", { ascending: false });

      if (pickupError) throw pickupError;

      if (!pickupData || pickupData.length === 0) {
        setPickups([]);
        setLoading(false);
        return;
      }

      // Fetch user profiles
      const userIds = [...new Set(pickupData.map((p: UserPickup) => p.user_id))];
      const { data: profileData } = await (supabase as any)
        .from("profiles")
        .select("id, email, first_name, last_name")
        .in("id", userIds);

      const profileMap = new Map();
      (profileData || []).forEach((p: any) => {
        const firstName = p.first_name || "";
        const lastName = p.last_name || "";
        const fullName = firstName && lastName 
          ? `${firstName} ${lastName}` 
          : p.email?.split("@")[0] || "Unknown";
        profileMap.set(p.id, { name: fullName, email: p.email });
      });

      // Fetch packages linked to these pickups
      const pickupIds = pickupData.map((p: UserPickup) => p.id);
      const { data: packageData } = await (supabase as any)
        .from("packages")
        .select("id, retailer, size, pickup_id")
        .in("pickup_id", pickupIds);

      const packagesByPickup = new Map<string, PackageInfo[]>();
      (packageData || []).forEach((pkg: any) => {
        const existing = packagesByPickup.get(pkg.pickup_id) || [];
        existing.push({ id: pkg.id, retailer: pkg.retailer, size: pkg.size });
        packagesByPickup.set(pkg.pickup_id, existing);
      });

      // Combine data
      const pickupsWithDetails: PickupWithDetails[] = pickupData.map((pickup: UserPickup) => {
        const profile = profileMap.get(pickup.user_id) || { name: "Unknown", email: "" };
        return {
          ...pickup,
          packages: packagesByPickup.get(pickup.id) || [],
          userName: profile.name,
          userEmail: profile.email,
        };
      });

      setPickups(pickupsWithDetails);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching counter-proposals:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounterProposals();
  }, []);

  const formatRetailerName = (retailer: string) => {
    const names: Record<string, string> = {
      "amazon-kohls": "Kohl's",
      "amazon-ups": "UPS",
      "amazon-fedex": "FedEx",
      "amazon-staples": "Staples",
      "amazon-wholefoods": "Whole Foods",
    };
    return names[retailer] || retailer;
  };

  const handleAcceptCounterProposal = async (pickup: PickupWithDetails) => {
    if (!pickup.counter_proposed_date || !pickup.counter_proposed_time_slot) {
      toast.error("No counter-proposal date/time to accept");
      return;
    }

    setActionLoading(true);
    try {
      // Update pickup with user's proposed date/time and confirm
      const { error } = await (supabase as any)
        .from("user_pickups")
        .update({
          proposed_date: pickup.counter_proposed_date,
          proposed_time_slot: pickup.counter_proposed_time_slot,
          status: "confirmed",
          counter_proposed_date: null,
          counter_proposed_time_slot: null,
        })
        .eq("id", pickup.id);

      if (error) throw error;

      // Update linked packages status
      await (supabase as any)
        .from("packages")
        .update({ status: "scheduled" })
        .eq("pickup_id", pickup.id);

      toast.success("Counter-proposal accepted! Pickup confirmed.");
      await fetchCounterProposals();
      onPickupUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to accept counter-proposal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenReschedule = (pickup: PickupWithDetails) => {
    setSelectedPickup(pickup);
    setNewDate(undefined);
    setNewTimeSlot("");
    setRescheduleDialogOpen(true);
  };

  const handleProposeNewTime = async () => {
    if (!selectedPickup || !newDate || !newTimeSlot) {
      toast.error("Please select a date and time");
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await (supabase as any)
        .from("user_pickups")
        .update({
          proposed_date: format(newDate, "yyyy-MM-dd"),
          proposed_time_slot: newTimeSlot,
          status: "awaiting_confirmation",
          denial_reason: null,
          user_message: null,
          counter_proposed_date: null,
          counter_proposed_time_slot: null,
        })
        .eq("id", selectedPickup.id);

      if (error) throw error;

      toast.success("New pickup time proposed. Awaiting user confirmation.");
      setRescheduleDialogOpen(false);
      setSelectedPickup(null);
      await fetchCounterProposals();
      onPickupUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to propose new time");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (pickups.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        <h3 className="font-semibold text-lg">Pickups Needing Rescheduling</h3>
        <Badge variant="secondary">{pickups.length}</Badge>
      </div>

      <div className="grid gap-4">
        {pickups.map((pickup) => (
          <Card key={pickup.id} className="border-amber-200/50">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <CardTitle className="text-base">{pickup.userName}</CardTitle>
                    <p className="text-sm text-muted-foreground">{pickup.userEmail}</p>
                  </div>
                </div>
                <Badge 
                  variant="outline" 
                  className={cn(
                    pickup.status === "counter_proposed" 
                      ? "bg-blue-100 text-blue-800 border-blue-200" 
                      : "bg-amber-100 text-amber-800 border-amber-200"
                  )}
                >
                  {pickup.status === "counter_proposed" ? "Counter-Proposed" : "User Denied"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Denial Reason */}
              {pickup.denial_reason && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm font-medium text-destructive">Reason:</p>
                  <p className="text-sm">{pickup.denial_reason}</p>
                </div>
              )}

              {/* User Message */}
              {pickup.user_message && (
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm font-medium text-muted-foreground">User Message:</p>
                  <p className="text-sm">{pickup.user_message}</p>
                </div>
              )}

              {/* Original vs Counter-Proposed */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Original Proposal */}
                <div className="p-3 rounded-lg bg-muted/50 border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Original Proposal</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {format(new Date(pickup.proposed_date), "MMM d, yyyy")}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" />
                      {pickup.proposed_time_slot}
                    </div>
                  </div>
                </div>

                {/* Counter-Proposal */}
                {pickup.counter_proposed_date && pickup.counter_proposed_time_slot && (
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="text-xs font-medium text-primary mb-2">User's Counter-Proposal</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {format(new Date(pickup.counter_proposed_date), "MMM d, yyyy")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5" />
                        {pickup.counter_proposed_time_slot}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Address */}
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <span className="text-muted-foreground">{pickup.address}</span>
              </div>

              {/* Packages */}
              <div className="flex items-center gap-2 flex-wrap">
                <Package className="h-4 w-4 text-muted-foreground" />
                {pickup.packages.map((pkg) => (
                  <Badge key={pkg.id} variant="secondary" className="text-xs">
                    {formatRetailerName(pkg.retailer)}
                  </Badge>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {pickup.counter_proposed_date && pickup.counter_proposed_time_slot && (
                  <Button
                    size="sm"
                    onClick={() => handleAcceptCounterProposal(pickup)}
                    disabled={actionLoading}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Accept User's Time
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenReschedule(pickup)}
                  disabled={actionLoading}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Propose New Time
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Propose New Pickup Time</DialogTitle>
            <DialogDescription>
              {selectedPickup && (
                <>Select a new date and time for {selectedPickup.userName}'s pickup.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Pickup Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !newDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newDate ? format(newDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newDate}
                    onSelect={setNewDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Time Window</Label>
              <Select value={newTimeSlot} onValueChange={setNewTimeSlot}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time window" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot} value={slot}>
                      {slot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleProposeNewTime} 
              disabled={!newDate || !newTimeSlot || actionLoading}
            >
              {actionLoading ? "Saving..." : "Propose Time"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
